import { AxiosResponse } from 'axios'
import { makeUMRequest } from '../utils/request'
import { ITag, IOrgMember, IOrgMemberWithTags } from '../utils/types'

const BASE_URL = '/orgadmin/tags'

class TagAPI {
  static async getAllTags(organizationId: number): Promise<ITag[]> {
    const res: AxiosResponse<ITag[]> = await makeUMRequest({
      method: 'GET',
      url: `${BASE_URL}`,
      params: { organizationId },
    })
    return res.data
  }

  static async getTagsByOrgMember(orgMemberId: number): Promise<ITag[]> {
    const res: AxiosResponse<ITag[]> = await makeUMRequest({
      method: 'GET',
      url: `${BASE_URL}/members/${orgMemberId}/tags`,
    })
    return res.data
  }

  static async getOrgMembersByTag(
    tagName: string,
    organizationId: number
  ): Promise<IOrgMember[]> {
    try {
      const encodedTagName = encodeURIComponent(tagName.trim())

      const res: AxiosResponse<IOrgMember[]> = await makeUMRequest({
        method: 'GET',
        url: `${BASE_URL}/${encodedTagName}/members`,
        params: { organizationId },
      })
      return Array.isArray(res.data) ? res.data : []
    } catch (error: unknown) {
      // Handle 404 errors gracefully - this is expected for tags with no members
      const err = error as { response?: { status?: number } }
      if (err?.response?.status === 404) {
        return []
      }
      // Re-throw other errors to be handled by the service layer
      throw error
    }
  }

  static async addTagToOrg(orgId: number, tagName: string): Promise<ITag> {
    const res: AxiosResponse<ITag> = await makeUMRequest({
      method: 'POST',
      url: `${BASE_URL}`,
      data: { organizationId: orgId, name: tagName.trim() },
    })
    return res.data
  }

  static async addOrgMemberToTag(
    tagId: number,
    orgMemberId: number
  ): Promise<void> {
    await makeUMRequest({
      method: 'POST',
      url: `${BASE_URL}/${tagId}/members/${orgMemberId}`,
    })
  }

  static async removeOrgMemberFromTag(
    tagId: number,
    orgMemberId: number
  ): Promise<void> {
    await makeUMRequest({
      method: 'DELETE',
      url: `${BASE_URL}/${tagId}/members/${orgMemberId}`,
    })
  }

  /**
   * GraphQL method to fetch all org members with their tags in a single optimized query
   * Uses batch mapping on the backend to eliminate N+1 query problem
   */
  static async getOrgMembersWithTags(
    organizationId: string
  ): Promise<IOrgMemberWithTags[]> {
    const query = `
      query GetOrgMembersWithTags($organizationId: ID!) {
        organization: organizationById(id: $organizationId) {
          id
          name
          members {
            id
            user {
              id
              username
              email
            }
            tag {
              id
              name
            }
          }
        }
      }
    `

    const res: AxiosResponse<{
      data: {
        organization: {
          id: string
          name: string
          members: IOrgMemberWithTags[]
        }
      }
    }> = await makeUMRequest({
      method: 'POST',
      url: '/admin/graphql',
      data: {
        query,
        variables: { organizationId },
      },
    })

    return res.data.data.organization.members
  }
}

export default TagAPI
